// api/series.ts
import { API_BASE, getAuthHeaders } from './config';

/** ===== Typy ===== */
export type Series = {
    id: number;
    title: string;
    description?: string | null;
    cover_image_url?: string | null;
    is_visible: boolean;
    is_premium: boolean;
    language?: string | null;
    created_by?: number | null;
    last_edited_by?: number | null;
    created_at: string;      // ISO
    last_edited_at: string;  // ISO
    cover_url?: string | null;
};

export type SeriesListResponse = {
    items: Series[];
    total: number;
    page: number;
    limit: number;
};

export type SeriesItem = {
    content_id: number;
    sort_index: number;
    title: string;
    type: string;
    language: string | null;
    is_visible: boolean;
    is_premium: boolean;
};

export type SeriesTranslation = {
    id: number;
    series_id: number;
    language_code: string;
    translated_title?: string | null;
    translated_description: string | null;
    is_visible: boolean;
    created_by?: number | null;
    last_edited_by?: number | null;
    created_at: string;
    last_edited_at: string;
};

/** ===== Listowanie serii (z filtrami/paginacją po stronie API) ===== */
export async function fetchSeries(params?: {
    query?: string;
    page?: number;
    limit?: number;
    is_visible?: 'any' | 'true' | 'false';
    is_premium?: 'any' | 'true' | 'false';
    language?: string;
}): Promise<SeriesListResponse> {
    const qp = new URLSearchParams();
    if (params?.query) qp.set('query', params.query);
    if (params?.page) qp.set('page', String(params.page));
    if (params?.limit) qp.set('limit', String(params.limit));
    if (params?.is_visible && params.is_visible !== 'any') qp.set('is_visible', params.is_visible);
    if (params?.is_premium && params.is_premium !== 'any') qp.set('is_premium', params.is_premium);
    if (params?.language) qp.set('language', params.language);

    const res = await fetch(`${API_BASE}/series?${qp.toString()}`, {
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

/** ===== Pojedyncza seria ===== */
export async function getSeries(id: number | string): Promise<Series> {
    const res = await fetch(`${API_BASE}/series/${id}`, {
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

/** ===== Utworzenie/aktualizacja/usunięcie ===== */
export async function createSeries(payload: {
    title: string;
    description?: string | null;
    is_visible?: boolean;
    is_premium?: boolean;
    language?: string | null;
    cover_image_url?: string | null;
}): Promise<Series> {
    const res = await fetch(`${API_BASE}/series`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function updateSeries(
    id: number | string,
    patch: Partial<{
        title: string;
        description: string | null;
        is_visible: boolean;
        is_premium: boolean;
        language: string | null;
        cover_image_url: string | null;
    }>
): Promise<Series> {
    const res = await fetch(`${API_BASE}/series/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(patch),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function deleteSeries(id: number | string): Promise<{ ok: true; id: number }> {
    const res = await fetch(`${API_BASE}/series/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

/** ===== Elementy serii (powiązane contents) ===== */
export async function listSeriesContents(seriesId: number | string): Promise<SeriesItem[]> {
    const res = await fetch(`${API_BASE}/series/${seriesId}/contents`, {
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function addSeriesContents(seriesId: number | string, contentIds: number[]): Promise<{ ok: true; added: number }> {
    const res = await fetch(`${API_BASE}/series/${seriesId}/contents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ content_ids: contentIds }),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function updateSeriesItemOrder(
    seriesId: number | string,
    contentId: number,
    sort_index: number
): Promise<{ id: number; series_id: number; content_id: number; sort_index: number }> {
    const res = await fetch(`${API_BASE}/series/${seriesId}/contents/${contentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ sort_index }),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function removeSeriesItem(seriesId: number | string, contentId: number): Promise<{ ok: true }> {
    const res = await fetch(`${API_BASE}/series/${seriesId}/contents/${contentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

// ---- MEDIA: COVER (Series) ----
export async function uploadSeriesCover(seriesId: number | string, file: File) {
    const fd = new FormData();
    fd.append('image', file);
    const res = await fetch(`${API_BASE}/series/${seriesId}/upload-image`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: fd,
    });
    if (!res.ok) throw await res.json();
    return res.json(); // { image: 'https://...' }
}

export async function deleteSeriesCover(seriesId: number | string, filename: string) {
    const res = await fetch(`${API_BASE}/series/${seriesId}/image/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function listSeriesTranslations(seriesId: number | string): Promise<SeriesTranslation[]> {
    const res = await fetch(`${API_BASE}/series/${seriesId}/translations`, {
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function getSeriesTranslation(seriesId: number | string, lang: string): Promise<SeriesTranslation> {
    const res = await fetch(`${API_BASE}/series/${seriesId}/translations/${encodeURIComponent(lang)}`, {
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function createSeriesTranslation(
    seriesId: number | string,
    payload: { language_code: string; translated_description?: string | null; is_visible?: boolean }
): Promise<SeriesTranslation> {
    const res = await fetch(`${API_BASE}/series/${seriesId}/translations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function upsertSeriesTranslation(
    seriesId: number | string,
    lang: string,
    payload: { translated_description?: string | null; is_visible?: boolean; translated_title?: string | null }
): Promise<SeriesTranslation> {
    const res = await fetch(`${API_BASE}/series/${seriesId}/translations/${encodeURIComponent(lang)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function patchSeriesTranslation(
    seriesId: number | string,
    lang: string,
    patch: Partial<{ translated_description: string | null; is_visible: boolean; translated_title: string | null }>
): Promise<SeriesTranslation> {
    const res = await fetch(`${API_BASE}/series/${seriesId}/translations/${encodeURIComponent(lang)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(patch),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}

export async function deleteSeriesTranslation(seriesId: number | string, lang: string): Promise<{ ok: true }> {
    const res = await fetch(`${API_BASE}/series/${seriesId}/translations/${encodeURIComponent(lang)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
}